import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { APIParameters } from '~/api/generic-api';

import { ReportApi } from '~/api/report-api';
import { Authentication } from '~/authentication/authentication';
import {
	ContentType,
	EmbedContentDialog,
	EmbedContentDialogModel,
} from '~/components/dialog/embed-content-dialog';
import { Notification } from '~/components/notification/notification';
import { TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import {
	ReportOutputType,
	ReportRecordType,
	ReportResult,
	RunReportResult,
} from '~/models/report-model';
import { getNativeTaskRunner } from '~/treeview/native-task-runner';
import { DialogUtils } from '~/utils/dialog-utils';

@autoinject
export class ReportOutputUtils {
	constructor(
		private reportApi: ReportApi,
		private i18n: I18N,
		private readonly authentication: Authentication,
		private readonly dialogUtils?: DialogUtils,
	) {}

	public displayReportOutput(
		reportOutput: ReportResult | RunReportResult,
		extraParams?: APIParameters,
		onClose?: () => void,
	): void {
		if (!reportOutput.id || !reportOutput.reportId) {
			throw new Error('Missing record id or report id');
		}

		const contentType = this.mapReportOutputTypeToMediaType(reportOutput);

		if (!contentType || contentType === ContentType.OCTET_STREAM) {
			Notification.info(this.i18n.tr('report:not-support-view-output'));
			return;
		}

		if (sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) !== '1') {
			if (contentType === ContentType.ACURITY_PRESENTER) {
				Notification.info(
					this.i18n.tr('report:not-support-presenter-output'),
				);
				return;
			} else if (contentType === ContentType.PCL) {
				Notification.info(
					this.i18n.tr('report:not-support-view-output'),
				);
				return;
			}
		}

		if (
			contentType === ContentType.PCL ||
			contentType === ContentType.ACURITY_PRESENTER
		) {
			const nativeTaskRunner = getNativeTaskRunner();
			if (nativeTaskRunner) {
				try {
					switch (contentType) {
						case ContentType.PCL:
							nativeTaskRunner.openPclFile(reportOutput.reportId);
							break;
						case ContentType.ACURITY_PRESENTER:
							nativeTaskRunner.openPresenterView(
								reportOutput.reportId,
							);
							break;
						default:
							Notification.error('Native function not found');
					}
				} catch (e) {
					Notification.error(e);
				}
			} else {
				Notification.warning(
					this.i18n.tr('report:native-task-runner-not-registered'),
				);
			}
			return;
		}

		const blobUrlPromise = this.reportApi.getReportOutputBlobUrl(
			reportOutput.id,
			extraParams,
		);

		// NOTE: WEBUI-3192
		// XML is not properly rendered in embed or iframe. Temperary solution is to open it in a new tab.
		// The drawback is that access token is in the URL and is visible to end user easily
		if (contentType === ContentType.XML) {
			blobUrlPromise.then((blobUrl) => {
				window.open(blobUrl);
			});
			return;
		}

		blobUrlPromise.then((blobUrl) => {
			const model: EmbedContentDialogModel = {
				contentSrc: blobUrl,
				contentType: contentType || ContentType.PLAIN_TEXT,
			};

			this.dialogUtils
				.showCustomDialog(EmbedContentDialog, model)
				.whenClosed((result) => {
					if (result.wasCancelled && onClose) {
						onClose();
					}
				});
		});
	}

	private mapReportOutputTypeToMediaType = (
		reportOutput: ReportResult,
	): ContentType => {
		// NOTE(Albert): 2020-02-07 - WEBUI-3003 If the report is of these types and RLc_Output_Type
		// is empty, we can safely assume they are text files. See reasoning on Jira description.
		const reportTypesMissingOutputType: ReportRecordType[] = [
			ReportRecordType.EXIT,
			ReportRecordType.REVIEW,
			ReportRecordType.QUOTE,
			ReportRecordType.NEW_MEMBER,
			ReportRecordType.SALARY_UPDATE,
			ReportRecordType.PLUGIN,
		];
		let reportOutputType: ReportOutputType = reportOutput.reportOutputType;
		if (
			reportTypesMissingOutputType.includes(
				reportOutput.reportRecordType,
			) &&
			reportOutputType.trim() === ''
		) {
			reportOutputType = ReportOutputType.TEXT;
		}

		switch (reportOutputType) {
			case ReportOutputType.TEXT:
				return ContentType.PLAIN_TEXT;
			case ReportOutputType.HTM:
			case ReportOutputType.HTML:
				return ContentType.HTML;
			case ReportOutputType.PDF:
				return ContentType.PDF;
			case ReportOutputType.XML:
				return ContentType.XML;
			case ReportOutputType.ACURITY_PRESENTER:
				return ContentType.ACURITY_PRESENTER;
			case ReportOutputType.PCL:
				return ContentType.PCL;

			default:
				const exhaustiveCheck: never = reportOutputType;
				if (DEVELOPMENT) {
					throw new Error(
						'Developer Bug: Unrecognised reportOutputType: ' +
							reportOutputType,
					);
				}
				break;
		}
		return undefined;
	};
}
